import React, { useState, useEffect } from 'react';
import Icon, { FilterFilled, WarningOutlined } from '@ant-design/icons';
import {
  Row, Col, Select, Button, Table, TablePaginationConfig,
  notification, Switch, Badge, Modal, Form,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import AccountGroupIcon from 'mdi-react/AccountGroupIcon';
import SquareEditOutlineIcon from 'mdi-react/SquareEditOutlineIcon';
import TrashCanIcon from 'mdi-react/TrashCanIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import {
  patch, put, post, isSuperAgentError,
  RequestWrapper, ResponseWrapper,
} from '../../../dataSource/requests';
import { RoleFromList } from '../../../sharedTypes/RoleTypes';
import { backendResponseCodes } from '../../../utils/responseMessages';
import { getUserInfoOnLocalStorage, getUserPermissions } from '../../../utils/users';
import './ListRoles.scss';

const AccountGroup = (props: any): React.ReactElement => {
  return (<Icon component={AccountGroupIcon} {...props} />);
};

const SquareEditOutline = (props: any): React.ReactElement => {
  return (<Icon component={SquareEditOutlineIcon} {...props} />);
};

const TrashCan = (props: any): React.ReactElement => {
  return (<Icon component={TrashCanIcon} {...props} />);
};

const ListRoles = (): React.ReactElement => {
  interface FormListRolesValues {
    userTypeFilter: 'COOPERATIVE' | 'COOPERCITRUS' | '';
  }

  interface RolesTableDefinition {
    loading: boolean;
  }

  interface RoleType {
    name: string;
    value: string;
  }

  interface RoleTablePagination {
    current?: number;
    defaultCurrent?: number;
    pageSize?: number;
    total?: number;
    showQuickJumper?: boolean | {
      goButton?: React.ReactNode;
    },
  }

  const [formInstance] = Form.useForm();
  const navigate = useNavigate();

  const [tableDefinition, setTableDefinition] = useState<RolesTableDefinition>({
    loading: false,
  });

  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);

  const [types] = useState<Array<RoleType>>([
    {
      name: 'Cooperado',
      value: 'COOPERATIVE',
    },
    {
      name: 'Coopercitrus',
      value: 'COOPERCITRUS',
    },
    {
      name: 'Todos',
      value: '',
    },
  ]);

  const [roles, setRoles] = useState<Array<RoleFromList>>([]);

  const [tablePagination, setTablePagination] = useState<RoleTablePagination>({
    current: 1,
    defaultCurrent: 1,
    pageSize: 1,
    showQuickJumper: true,
    total: 0,
  });

  const exportExcel = async (): Promise<void> => {
    setLoadingExcel(true);

    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const paramsFilter = formInstance.getFieldsValue();

    const requestReference: RequestWrapper = post({
      url: '/role/export/excel',

      params: {
        type: paramsFilter.userTypeFilter !== '' ? paramsFilter.userTypeFilter : null,
      },

      responseType: 'blob',

      headers: {
        'Content-Type': 'application/json',
        Accept: '*/*',
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      console.log(error);
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.status === 200): {
        const fileBlob = new Blob([response?.body], { type: 'xlsx' });

        const linkElement = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(fileBlob);

        const today = new Date();
        const date = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
        linkElement.href = downloadUrl;
        linkElement.download = `Perfis ${date}.xlsx`;

        document.body.append(linkElement);

        linkElement.click();
        setLoadingExcel(false);
        setTimeout(() => {
          linkElement.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }, 0);
      } break;

      case (responseError?.status === 401):
        // tratado no requests.ts
        break;

      case (responseError?.status === 403):
        notification.error({
          message: 'Erro',
          description: 'Você não possui permissão para realizar esta ação.',
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um problema ao baixar o arquivo, por favor tente novamente mais tarde.',
        });
        break;
    }

    setLoadingExcel(false);
  };

  const executeFilter = async (values: FormListRolesValues | null, page = 1): Promise<void> => {
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;
    let newValues = values;

    if (!newValues) {
      try {
        newValues = await formInstance.validateFields();
      } catch (error: unknown) {
        console.log(error);
        return;
      }
    }

    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: true,
      });
    });

    const requestReference: RequestWrapper = patch({
      url: '/role/listAllFilteredRoles',

      query: {
        page: (page >= 1) ? page - 1 : 0,
      },

      params: {
        type: newValues?.userTypeFilter || null,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error: unknown) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    switch (true) {
      case (response?.body?.code === 3003):
        // "All roles listed successfully", 3003
        setRoles([...response?.body.roles]);
        setTablePagination((previous) => {
          return ({
            ...previous,
            current: response?.body.paginationInfo.pageNumber + 1,
            pageSize: response?.body.paginationInfo.maxSize,
            total: response?.body.paginationInfo.totalElements,
          });
        });
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        notification.error({
          message: 'Erro',
          description: backendResponseCodes.Error.General.Forbidden.description.pt_br,
        });
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, não foi possível carregar a listagem de perfis, por favor tente novamente mais tarde.',
        });
        break;
    }

    setTableDefinition((previous) => {
      return ({
        ...previous,
        loading: false,
      });
    });
  };

  const onChangeDefaultRole = (eventt: boolean, record: RoleFromList): void => {
    Modal.confirm({
      title: 'Atenção',
      content: `Você tem certeza que deseja mudar o perfil padrão para ${record.name} ?`,
      okText: 'Sim, alterar',
      cancelText: 'Não, cancelar',
      autoFocusButton: null,
      icon: (<WarningOutlined className="ListRoles__ChangeDefaultRoleConfirmIcon" />),

      onOk: async () => {
        let response: ResponseWrapper | undefined;
        let responseError: ResponseWrapper | undefined;
        let genericError: Error | undefined;

        roles.forEach((value: RoleFromList) => {
          const newValue = value;
          if (value.id === record.id) {
            newValue.defaultRole = eventt;
            newValue.loading = true;
          }
        });

        setRoles(roles);

        const requestReference: RequestWrapper = put({
          url: `/role/setDefaultRole/${record.id}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        try {
          response = await requestReference;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            responseError = error.response;
          } else {
            genericError = error as Error;
          }
        }

        switch (true) {
          case (response?.body?.code === 3011):
            // "Default role set successfully", 3011
            notification.success({
              message: 'Sucesso',
              description: 'Perfil alterado',
            });
            break;

          case (responseError?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (responseError?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Error',
              description: backendResponseCodes.Error.General
                .Forbidden.description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3000):
            // "Role not found", -3000
            notification.error({
              message: 'Error',
              description: backendResponseCodes.Error.Role.RoleNotFound
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3006):
            // "A deactivated role cannot be default", -3006
            notification.error({
              message: 'Error',
              description: backendResponseCodes.Error.Role
                .ADeactivatedRoleCannotBeDefault
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3008):
            // "Cannot edit system admin role", -3008
            notification.error({
              message: 'Error',
              description: backendResponseCodes.Error.Role
                .CannotEditSystemAdminRole
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3018):
            // "Role already default", -3018
            roles.forEach((value: RoleFromList) => {
              const newValue = value;
              if (value.id === record.id) {
                newValue.defaultRole = !eventt;
                newValue.loading = false;
              }
            });

            setRoles(roles);

            notification.error({
              message: 'Error',
              description: backendResponseCodes.Error.Role
                .RoleAlreadyDefault
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -4000):
            // "Something unexpected happened", -4000
            notification.error({
              message: 'Error',
              description: backendResponseCodes.Error.General
                .SomethingUnexpectedHappened
                .description.pt_br,
            });
            break;

          case (genericError?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um problema ao realizar esta ação, tente novamente por favor.',
            });
            break;
        }

        executeFilter(null, tablePagination.current);
      },
    });
  };

  const handleTableChange = (pagination: TablePaginationConfig): void => {
    setTablePagination((previous: RoleTablePagination) => {
      return ({
        ...previous,
        current: pagination.current,
        defaultCurrent: pagination.defaultCurrent,
        pageSize: pagination.pageSize,
        showQuickJumper: pagination.showQuickJumper,
        total: pagination.total,
      });
    });

    executeFilter(null, pagination.current);
  };

  const editButtonTitle = (record: RoleFromList): string => {
    switch (true) {
      case (record.id === 2):
        return 'O Perfil Administrador do sistema não pode ser editado';

      case (record.id === getUserInfoOnLocalStorage().role.id):
        return 'Impossível editar: O Perfil está vinculado ao usuário logado';

      default:
        return 'Editar';
    }
  };

  const deleteButtonTitle = (record: RoleFromList): string => {
    switch (true) {
      case (record.id === 2):
        return 'O Perfil Administrador do sistema não pode ser excluído';

      case (record.id === getUserInfoOnLocalStorage().role.id):
        return 'Impossível excluir: O Perfil está vinculado ao usuário logado';

      case (record.hasUsers):
        return 'Impossível excluir: O Perfil contém usuários vinculados';

      default:
        return 'Excluir';
    }
  };

  const switchButtonTitle = (record: RoleFromList): string => {
    switch (true) {
      case (record.status === 'DEACTIVATED'):
        return 'Impossível tornar padrão: Perfil inativo';

      case (record.defaultRole === true):
        return 'Torne outro Perfil padrão para desmarcar o atual';

      default:
        return 'Clique para tornar o Perfil padrão';
    }
  };

  const showDeleteConfirm = (roleID: number): void => {
    Modal.confirm({
      title: 'Atenção',
      content: 'Deseja realmente excluir este perfil?',
      okText: 'Sim, excluir',
      cancelText: 'Não, cancelar',
      autoFocusButton: null,
      icon: (<WarningOutlined className="ListRoles__DeleteRoleConfirmWarningIcon" />),

      onOk: async () => {
        let response: ResponseWrapper | undefined;
        let responseError: ResponseWrapper | undefined;
        let genericError: Error | undefined;

        const requestReference: RequestWrapper = put({
          url: `/role/delete/${roleID}`,

          frontConfigs: {
            navigateFunction: navigate,
          },
        });

        try {
          response = await requestReference;
        } catch (error: unknown) {
          if (isSuperAgentError(error)) {
            responseError = error.response;
          } else {
            genericError = error as Error;
          }
        }

        switch (true) {
          case (response?.body?.code === 3006):
            // "Role deleted successfully", 3006
            notification.success({
              message: 'Sucesso',
              description: backendResponseCodes.Success.Role
                .RoleDeletedSuccessfully.description.pt_br,
            });
            break;

          case (responseError?.body?.code === -4001):
            // "You must be authenticated to perform this operation", -4001
            break;

          case (responseError?.body?.code === -4003):
            // "You do not have permission to perform this operation", -4003
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.General
                .Forbidden
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3012):
            // "Cannot delete system admin role", -3012
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.Role
                .CannotDeleteSystemAdminRole
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3000):
            // "Role not found", -3000
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.Role.RoleNotFound
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3011):
            // "Cannot delete default role ", -3011
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.Role
                .CannotDeleteDefaultRole
                .description.pt_br,
            });
            break;

          case (responseError?.body?.code === -3009):
            // "Cannot delete role used at least by one user", -3009
            notification.error({
              message: 'Erro',
              description: backendResponseCodes.Error.Role
                .CannotDeleteRoleUsedAtLeastByOneUser
                .description.pt_br,
            });
            break;

          case (genericError?.message?.includes('has been aborted')):
            // dont show anything when request is aborted
            break;

          default:
            notification.error({
              message: 'Erro',
              description: 'Desculpe, houve um problema ao excluir o perfil, por favor tente novamente mais tarde.',
            });
            break;
        }

        executeFilter(null, tablePagination.current);
      },
    });
  };

  useEffect(() => {
    formInstance.submit();

    return () => {
      setLoadingExcel(false);
    };
  }, []);

  return (
    <div className="ListRoles">
      <div className="ListRoles__ScreenTitle screen-title">
        Perfis de usuário
      </div>
      <Row gutter={24} className="ListRoles__FormRow row-wrapper">
        <Col
          className="ListRoles__FormRowMainColumn"
          xs={{ span: 24 }}
          lg={{ span: 24 }}
        >
          <Form
            scrollToFirstError
            className="ListRoles__Form"
            onFinish={
              (values): void => {
                executeFilter(values, 1);
              }
            }
            form={formInstance}
            initialValues={{
              userTypeFilter: '',
            }}
          >
            <Row
              className="ListRoles__FormFirstRow"
              gutter={24}
            >
              <Col
                className="ListRoles__UserTypeFilterColumn"
                xs={{ span: 24 }}
                lg={{ span: 6 }}
              >
                <Form.Item
                  className="ListRoles__UserTypeFilterFormItem"
                  label=""
                  name="userTypeFilter"
                  rules={[]}
                >
                  <Select
                    showSearch
                    className="ListRoles__UserTypeFilterSelect role-select"
                    size="large"
                    placeholder="Tipo"
                  >
                    {
                      types.map(
                        (type) => (
                          <Select.Option
                            className="ListRoles__UserTypeFilterSelectOption"
                            key={type.value}
                            value={type.value}
                          >
                            {type.name}
                          </Select.Option>
                        ),
                      )
                    }
                  </Select>
                </Form.Item>
              </Col>
              <Col
                lg={{ span: 3 }}
                className="ListRoles__FormEmptyColumn"
              />
              <Col
                xs={{ span: 24 }}
                lg={{ span: 5 }}
                className="ListRoles__FormExcelExportColumn"
              >
                <Form.Item
                  className="ListRoles__FormExcelExportFormItem"
                >
                  <Button
                    htmlType="submit"
                    className="ListRoles__FormSubmitButton main-buttons"
                  >
                    <FilterFilled />
                    Filtrar
                  </Button>
                </Form.Item>
              </Col>
              <Col
                className="ListRoles__SubmitColumn"
                xs={{ span: 24 }}
                lg={{ span: 5 }}
              >
                <Form.Item
                  className="ListRoles__FormSubmitFormItem"
                >
                  <Button
                    onClick={exportExcel}
                    loading={loadingExcel}
                    className="ListRoles__FormExcelExportButton main-buttons"
                  >
                    Exportar
                  </Button>
                </Form.Item>
              </Col>
              <Col
                className="ListRoles__NewRoleColumn"
                xs={{ span: 24 }}
                lg={{ span: 5 }}
              >
                <Form.Item
                  className="ListRoles__NewRoleFormItem"
                >
                  <Button
                    icon={<PlusIcon className="ListRoles__NewRoleButtonIcon" size={21} />}
                    onClick={
                      (): void => {
                        navigate('/roles/create');
                      }
                    }
                    className="ListRoles__NewRoleButton main-buttons"
                    htmlType="button"
                  >
                    Criar novo perfil
                  </Button>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>

      <Row
        className="ListRoles__TableParentRow list-container"
      >
        <Col
          span={24}
          className="ListRoles__TableParentColumn"
        >
          <Table
            rowKey="id"
            className="ListRoles__Table"
            columns={[
              {
                title: <div className="ListRoles__TableIDColumnTitle table-titles">ID</div>,
                dataIndex: 'id',
                key: 'id',
                className: 'ListRoles__TableIDColumn table-name-column',
              },
              {
                title: <div className="ListRoles__TableNameColumnTitle table-titles">Nome do perfil</div>,
                dataIndex: 'name',
                key: 'name',
                className: 'ListRoles__TableNameColumn table-name-column',
              },
              {
                title: <div className="ListRoles_TableTypeColumnTitle table-titles">Tipo</div>,
                className: 'ListRoles__TableTypeColumn',
                dataIndex: 'type',
                key: 'type',

                render: (text): React.ReactNode => {
                  switch (text) {
                    case 'COOPERATIVE':
                      return 'Cooperado';

                    case 'COOPERCITRUS':
                      return 'Coopercitrus';

                    default:
                      return '';
                  }
                },
              },
              {
                title: <div className="ListRoles__TableDefaultRoleColumnTitle table-titles">Perfil padrão</div>,
                className: 'ListRoles__TableDefaultRoleColumn',
                dataIndex: 'defaultRole',
                key: 'defaultRole',

                render: (text, record: RoleFromList) => (
                  <Switch
                    className="ListRoles__MakeDefaultSwitch make-default-switch"
                    disabled={text === true || record.status === 'DEACTIVATED'}
                    checked={text}
                    onChange={
                      (eventt): void => {
                        onChangeDefaultRole(eventt, record);
                      }
                    }
                    loading={record.loading || false}
                    title={switchButtonTitle(record)}
                  />
                ),
              },
              {
                title: <div className="ListRoles__TableStatusColumnTitle table-titles">Status</div>,
                dataIndex: 'status',
                key: 'status',
                className: 'ListRoles__TableStatusColumn',

                render: (text): React.ReactNode => {
                  let actualColor = '';
                  let actualText = '';

                  switch (true) {
                    case (text === 'ACTIVATED'):
                      actualColor = '#0076ff';
                      actualText = 'Ativo';
                      break;

                    case (text === 'DEACTIVATED'):
                      actualColor = '#b2b2b2';
                      actualText = 'Inativo';
                      break;

                    default:
                      actualColor = '#b2b2b2';
                      break;
                  }

                  return (<Badge className="ListRoles__TableStatusColumnBadge" color={actualColor} text={actualText} />);
                },
              },
              {
                title: <div className="ListRoles__TableActionsColumnTitle table-titles">Ações</div>,
                dataIndex: 'details',
                className: 'ListRoles__TableOptionsColumn',
                key: 'details',
                align: 'center',

                render: (_text, record) => (
                  <span className="ListRoles__OptionsButtonsParentSpan">
                    {
                      ((getUserPermissions() || []).includes('Usuários do perfil')) ? (
                        <Button
                          type="link"
                          size="small"
                          onClick={
                            (): void => {
                              navigate(`/profile/associate/${record.id}`);
                            }
                          }
                          className="ListRoles__TableOptionsColumnAssociateButton action-button"
                          title="Usuarios"
                        >
                          <AccountGroup className="ListRoles__TableOptionsColumnAssociateButtonIcon" />
                        </Button>
                      ) : null
                    }

                    <Button
                      type="link"
                      size="small"
                      onClick={
                        (): void => {
                          navigate(`/roles/${record.id}/edit`);
                        }
                      }
                      className="ListRoles__EditButton action-button"
                      title={editButtonTitle(record)}
                      disabled={
                        (record.id === 2)
                        || (record.id === getUserInfoOnLocalStorage().role.id)
                      }
                    >
                      <SquareEditOutline className="ListRoles__EditButtonIcon" />
                    </Button>
                    <Button
                      type="link"
                      size="small"
                      onClick={
                        (): void => {
                          showDeleteConfirm(record.id);
                        }
                      }
                      className="ListRoles__DeleteButton action-button"
                      title={deleteButtonTitle(record)}
                      disabled={
                        (record.id === 2)
                        || (record.id === getUserInfoOnLocalStorage().role.id)
                        || record.hasUsers
                      }
                    >
                      <TrashCan className="ListRoles__DeleteButtonIcon" />
                    </Button>
                  </span>
                ),
              },
            ]}
            dataSource={roles}
            rowClassName="table-row"
            loading={tableDefinition.loading}
            pagination={{
              defaultCurrent: tablePagination.defaultCurrent || 1,
              current: tablePagination.current || 1,
              pageSize: tablePagination.pageSize || 15,
              total: tablePagination.total || 0,
              showQuickJumper: tablePagination.showQuickJumper || true,
            }}
            onChange={handleTableChange}
          />
        </Col>
      </Row>
    </div>
  );
};

ListRoles.propTypes = {};
ListRoles.defaultProps = {};

export default ListRoles;
